<template>
  <div class="dress-code-section" :dir="settingStore.language === 'ar' ? 'rtl' : 'ltr'">
    <Banner
      image="https://images.unsplash.com/photo-1529139574466-a303027c1d8b?q=80&w=2787&auto=format&fit=crop"
      :title="$t('dress_code.title')"
      height="700px"
    />
    <!-- Wave Effect -->
    <div class="wave-container">
      <svg class="wave-top" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 1440 400" preserveAspectRatio="none">
        <path class="wave-path" d="M0,96L40,106.7C80,117,160,139,240,154.7C320,171,400,181,480,165.3C560,149,640,107,720,90.7C800,75,880,85,960,112C1040,139,1120,181,1200,181.3C1280,181,1360,139,1400,117.3L1440,96L1440,400L1400,400C1360,400,1280,400,1200,400C1120,400,1040,400,960,400C880,400,800,400,720,400C640,400,560,400,480,400C400,400,320,400,240,400C160,400,80,400,40,400L0,400Z"></path>
      </svg>
    </div>

    <!-- Content Container -->
    <div class="container">
      <!-- Loading state -->
      <div v-if="loading" class="loading-state">
        <div class="loading-spinner"></div>
        <p>{{ $t('dress_code.loading') }}</p>
      </div>
      
      <!-- Error state -->
      <div v-if="errorMessage" class="error-state">
        <i class="fas fa-exclamation-circle"></i>
        <p>{{ $t('dress_code.error_message') }}</p>
        <button @click="fetchDressCode" class="retry-btn">
          <i class="fas fa-redo"></i> Retry
        </button>
      </div>
      
      <!-- Categories -->
      <div v-if="dresscode" class="categories-grid">
        <h2 class="section-title">{{ $t('dress_code.categories') }}</h2>
        <div 
          v-for="(item, index) in dresscode.category" 
          :key="index" 
          class="category-card"
          :class="{ 'active': selectedCategory === item.id }"
          @click="filterDresscode(item.id)"
        >
          <div class="category-image">
            <div class="image-wrapper">
              <img 
                :src="item.image || '/api/placeholder/200/200'"
                :alt="currentLanguage === 'en' ? item.name_en : item.name_ar"
                @error="handleImageError"
                loading="lazy"
              />
            </div>
          </div>
          <h3>{{ currentLanguage === 'en' ? item.name_en : item.name_ar }}</h3>
        </div>
      </div>
      
      <!-- Items -->
      <div v-if="!loading && dresscodeItems.length > 0" class="items-grid">
        <h2 class="section-title">{{ $t('dress_code.items') }}</h2>
        <div 
          v-for="(item, index) in dresscodeItems" 
          :key="index" 
          class="item-card"
        >
          <div class="item-image">
            <div class="image-wrapper">
              <img 
                :src="item.image || '/api/placeholder/400/300'"
                :alt="currentLanguage === 'en' ? item.name_en : item.name_ar"
                @error="handleImageError"
                loading="lazy"
              />
            </div>
          </div>
          <div class="item-content">
            <h4>{{ currentLanguage === 'en' ? item.name_en : item.name_ar }}</h4>
            <!-- <div class="item-status" :class="item.status">
              <i :class="getStatusIcon(item.status)"></i>
              {{ item.status === 'allowed' ? 'Allowed' : 'Not Allowed' }}
            </div> -->
          </div>
        </div>
      </div>

      <!-- No items state -->
      <div v-if="!loading && dresscodeItems.length === 0" class="no-items-state">
        <i class="fas fa-search"></i>
        <p>No dress code items found for this category</p>
      </div>
    </div>
  </div>
</template>

<script setup>
import Banner from "@/components/Banner.vue";
import { onMounted, ref, computed, watchEffect } from "vue";
import axiosInstance from "@/plugins/axios";
import { useSettingStore } from '@/stores/settingStore';
import { useI18n } from 'vue-i18n';

const settingStore = useSettingStore();
const { locale } = useI18n();

// Compute current language from store
const currentLanguage = computed(() => settingStore.language);

// Watch for language changes and update i18n locale
watchEffect(() => {
  locale.value = currentLanguage.value;
  document.documentElement.setAttribute('dir', currentLanguage.value === 'ar' ? 'rtl' : 'ltr');
});

const dresscode = ref(null);
const loading = ref(true);
const errorMessage = ref('');
const dresscodeItems = ref([]);
const selectedCategory = ref(1);

const handleImageError = (event) => {
  event.target.src = '/api/placeholder/400/300';
};



// const getStatusIcon = (status) => {
//   return status === 'allowed' ? 'fas fa-check-circle' : 'fas fa-times-circle';
// };

const fetchDressCode = async () => {
  try {
    loading.value = true;
    errorMessage.value = '';
    const response = await axiosInstance.get("/get-dress-code");
    dresscode.value = response.data;
    filterDresscode(1);
  } catch (error) {
    console.error('API Error:', error);
    errorMessage.value = "Failed to load dress code guidelines.";
  } finally {
    loading.value = false;
  }
};

const filterDresscode = (id) => {
  selectedCategory.value = id;
  if (dresscode.value && dresscode.value.items) {
    dresscodeItems.value = dresscode.value.items.filter(
      item => item.app_dress_code_category_id === id
    );
  }
};

onMounted(() => {
  fetchDressCode();
});
</script>
<style scoped>
.dress-code-section {
  position: relative;
  background: white;
  margin-top: 700px;
}

.wave-container {
  position: absolute;
  top: -200px;
  left: 0;
  width: 100%;
  height: 220px; 
  overflow: hidden;
}

.wave-top {
  position: absolute;
  bottom: -20px; /* Negative bottom value to ensure overlap */
  left: 0;
  width: 100%;
  height: 100%;
  transform-origin: bottom;
  animation: mainWaveFloat 5s ease-in-out infinite alternate;
}

.wave-path {
  fill: #ffffff;
  filter: drop-shadow(0px -2px 2px rgba(0, 0, 0, 0.1));
}

.categories-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 20px;
  justify-content: center;
  align-items: center;
  text-align: center;
  max-width: 800px; /* Adjust width if needed */
  margin: 0 auto; /* Center the grid */
}


.category-card {
  background: white;
  border-radius: 12px;
  padding: 15px;
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: 0 2px 8px rgba(0,0,0,0.1);
  text-align: center;
}

.category-card:hover {
  transform: translateY(-5px);
  box-shadow: 0 4px 12px rgba(0,0,0,0.15);
}

.category-card.active {
  border: 2px solid #007bff;
  background: #f8f9ff;
}

.category-image {
  width: 100px;
  /* height: 100px; */
  margin: 0 auto 15px;
  overflow: hidden;
  border-radius: 8px;
}

.category-image img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.category-card h3 {
  font-size: 1.1rem;
  color: #333;
  margin: 0;
}

.items-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 25px;
  margin-top: 30px;
}

.item-card {
  background: white;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 2px 8px rgba(0,0,0,0.1);
  transition: transform 0.3s ease;
}

.item-card:hover {
  transform: translateY(-5px);
}

.item-image {
  width: 100%;
  /* height: 200px; */
  overflow: hidden;
}

.item-image img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.item-card h4 {
  padding: 15px;
  margin: 0;
  font-size: 1rem;
  color: #333;
}

.container {
  max-width: 1200px;
  margin: 0 auto;
  padding: 40px 20px;
}
@keyframes mainWaveFloat {
  from {
    transform: translateY(0) scaleY(1);
  }
  to {
    transform: translateY(10px) scaleY(1.3);
  }
}
@media (max-width: 768px) {
  .categories-grid {
    grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
    gap: 15px;
  }

  .items-grid {
    grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
    gap: 15px;
  }

  .category-image {
    width: 80px;
    height: 80px;
  }

  .item-image {
    height: 150px;
  }
}
.section-title {
  width: 100%;
  color: #1268B1;
  margin-bottom: 20px;
  font-size: 1.5rem;
  font-weight: 600;
  grid-column: 1 / -1;
}

[dir="rtl"] .category-card,
[dir="rtl"] .item-card {
  text-align: right;
}

[dir="rtl"] .item-status {
  flex-direction: row-reverse;
}

[dir="rtl"] .retry-btn i {
  margin-left: 8px;
  margin-right: 0;
}

[dir="rtl"] .loading-state,
[dir="rtl"] .error-state,
[dir="rtl"] .no-items-state {
  direction: rtl;
}
</style>